import { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, RefreshCw, Check, BookOpen } from "lucide-react";

const haptic = () => navigator.vibrate?.(8);

export const Lesson = ({ world, accent, onComplete }) => {
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [done, setDone] = useState(false);

  const fetchLesson = async (refresh = false) => {
    setLoading(true);
    try {
      const { data } = await api.post("/alignment/lesson", { world_id: world.id, refresh });
      setLesson(data.lesson);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { setDone(false); fetchLesson(); /* eslint-disable-next-line */ }, [world.id]);

  const complete = async () => {
    haptic();
    try {
      const { data } = await api.post("/alignment/complete", { kind: "lesson", world_id: world.id });
      setDone(true);
      toast.success("Lesson absorbed.");
      onComplete?.(data);
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (loading) return <Skeleton />;
  if (!lesson) return null;

  return (
    <div data-testid="lesson-content">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 text-sm" style={{ color: accent }}><BookOpen className="w-4 h-4" /> Lesson</div>
        <button data-testid="refresh-lesson-btn" onClick={() => fetchLesson(true)} className="text-xs text-slate-400 hover:text-slate-200 inline-flex items-center gap-1 transition-colors duration-300">
          <RefreshCw className="w-3.5 h-3.5" /> New lesson
        </button>
      </div>
      <h3 className="font-head font-700 text-2xl text-slate-50 mb-3">{lesson.title}</h3>
      {lesson.insight && <p className="text-base text-slate-200 leading-relaxed mb-4 italic">{lesson.insight}</p>}
      <div className="space-y-3 mb-5">
        {(lesson.teaching || []).map((p, i) => (
          <p key={i} className="text-sm text-slate-300 leading-relaxed">{p}</p>
        ))}
      </div>
      {lesson.takeaway && (
        <div className="rounded-xl border p-4 mb-4" style={{ borderColor: `${accent}40`, background: `${accent}0d` }}>
          <p className="text-sm text-slate-100"><span className="uppercase tracking-wider text-xs" style={{ color: accent }}>Takeaway · </span>{lesson.takeaway}</p>
        </div>
      )}
      {lesson.question && <p className="text-sm text-slate-400 mb-5">Reflect: {lesson.question}</p>}
      <button
        data-testid="complete-lesson-btn"
        onClick={complete}
        disabled={done}
        className="text-sm inline-flex items-center gap-2 transition-colors duration-300 disabled:opacity-60"
        style={{ color: accent }}
      >
        <Check className="w-4 h-4" /> {done ? "Completed" : "Mark lesson complete"}
      </button>
    </div>
  );
};

const Skeleton = () => (
  <div className="flex items-center gap-3 text-slate-400 py-8"><Loader2 className="w-5 h-5 animate-spin" /> Gathering wisdom…</div>
);
